import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { MatrixRain } from "./MatrixRain";
import styles from "./BootSequence.module.css";

interface BootSequenceProps {
  onComplete: () => void;
}

const LINES = [
  "INITIALISING…",
  "CORE SYSTEMS……………OK",
  "INTELLIGENCE LAYER……ONLINE",
  "RIG VAULT…………………SEALED",
  "ACQUIRER…………………ACME ACQUIRING",
  "PORTFOLIO………………30 MERCHANTS",
  "READY _",
];

const MS_PER_CHAR = 12;
const POST_LINE_PAUSE = 70;
const FADE_MS = 420;

export function BootSequence({ onComplete }: BootSequenceProps) {
  const reduced = useReducedMotion();
  const [typed, setTyped] = useState<string[]>([]);
  const [currentLine, setCurrentLine] = useState("");
  const [fadingOut, setFadingOut] = useState(false);
  const skippedRef = useRef(false);

  useEffect(() => {
    if (reduced) {
      setTyped(LINES);
      const t = setTimeout(() => onComplete(), 300);
      return () => clearTimeout(t);
    }

    let lineIdx = 0;
    let charIdx = 0;
    let timeout: ReturnType<typeof setTimeout>;

    const tick = () => {
      if (skippedRef.current) return;
      if (lineIdx >= LINES.length) {
        setFadingOut(true);
        timeout = setTimeout(onComplete, FADE_MS);
        return;
      }
      const line = LINES[lineIdx];
      if (charIdx < line.length) {
        charIdx++;
        setCurrentLine(line.slice(0, charIdx));
        timeout = setTimeout(tick, MS_PER_CHAR);
      } else {
        setTyped((prev) => [...prev, line]);
        setCurrentLine("");
        charIdx = 0;
        lineIdx++;
        timeout = setTimeout(tick, POST_LINE_PAUSE);
      }
    };

    timeout = setTimeout(tick, 340);

    const skip = () => {
      if (skippedRef.current) return;
      skippedRef.current = true;
      clearTimeout(timeout);
      setTyped(LINES);
      setCurrentLine("");
      setFadingOut(true);
      setTimeout(onComplete, reduced ? 0 : 220);
    };

    window.addEventListener("keydown", skip);
    window.addEventListener("click", skip);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("keydown", skip);
      window.removeEventListener("click", skip);
    };
  }, [reduced, onComplete]);

  return (
    <div className={`${styles.boot} ${fadingOut ? styles.fadingOut : ""}`}>
      <MatrixRain opacity={0.18} layer="bg" />
      <div className={styles.scanline} aria-hidden />
      <div className={styles.inner}>
        <div className={`${styles.bootLogo} logo-glow`}>
          <img src="/ripper-logo.svg" alt="ripper" className={styles.bootLogoSvg} />
        </div>
        <div className={styles.bootSubtitle}>bite back · acme acquiring</div>
        <div className={styles.stack}>
          {typed.map((line, i) => (
            <div key={i} className={styles.line}>
              {line}
            </div>
          ))}
          {currentLine && (
            <div className={styles.line}>
              {currentLine}
              <span className={styles.cursor}>▍</span>
            </div>
          )}
        </div>
        <div className={styles.footer}>
          <span>▾ bite back · build 0.1</span>
          <span className={styles.tipDim}>any key to skip</span>
        </div>
      </div>
    </div>
  );
}
